import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, AlertCircle, Loader2, FileCheck, CheckCircle2, ShieldCheck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!token) { setError("This reset link is invalid or incomplete."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page" data-testid="reset-password-page">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <div className="lp-logo-icon"><FileCheck size={16} /></div>
            <span className="auth-logo">ResumeAI</span>
          </Link>
          <h1>{done ? "Password reset" : "Set a new password"}</h1>
          <p>{done ? "Redirecting you to sign in…" : "Choose a strong new password for your account."}</p>
        </div>

        {error && (
          <div className="auth-error" data-testid="reset-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {done ? (
          <div className="auth-form" data-testid="reset-success">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "12px 0" }}>
              <CheckCircle2 size={44} style={{ color: "#10b981" }} />
              <p style={{ textAlign: "center", color: "var(--text-secondary)", fontSize: "0.9rem", margin: 0 }}>
                Your password has been updated. You can now sign in with your new password.
              </p>
            </div>
            <Link to="/login" className="auth-btn" data-testid="goto-login-after-reset" style={{ textDecoration: "none" }}>
              <ShieldCheck size={16} /> Go to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label htmlFor="password">New password</label>
              <div className="auth-input-wrap">
                <Lock size={16} className="auth-input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password"
                />
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="confirm">Confirm new password</label>
              <div className="auth-input-wrap">
                <Lock size={16} className="auth-input-icon" />
                <input
                  id="confirm"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-confirm"
                />
              </div>
            </div>
            <button type="submit" className="auth-btn" disabled={loading} data-testid="reset-submit">
              {loading ? <Loader2 size={18} className="spin" /> : <ShieldCheck size={18} />}
              {loading ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}

        <div className="auth-footer">
          Remembered it? <Link to="/login" data-testid="reset-back-to-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
